/* eslint-disable max-lines -- Pre-existing violation */
import {
  Announcements,
  defaultAnnouncements,
  DndContext,
  DragOverlay,
  PointerSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {DataStatus, DropdownItem, DropdownSubmenu, showNotification} from 'platform/components';
import {Box, Heading, Stack, Icon, Show, Spinner, Text, VStack} from 'platform/foundation';
import {Lightbox} from 'platform/lightbox';
import {useDateTimeFormatter} from 'platform/locale';

import {useCallback, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';

import {ascend, descend, groupBy, head, isNil, isNotNil, partition, sortWith} from 'ramda';
import {isNilOrEmpty, isNonEmptyArray, isNotNilOrEmpty, isTrue} from 'ramda-adjunct';

import {useSubscribeToNotificationsQuery} from '@dms/api/appsync';
import {useGetParticipationQuery, useGetPhotoCheckerConfigQuery} from '@dms/api/core';
import {useGetVehicleQuery, useGetWatermarkListQuery} from '@dms/api/sales';
import {EntityResourceIds, VehicleAlbums} from '@dms/api/shared';
import {
  saleVehiclePhotoApi,
  useCopySaleVehiclePhotoMutation,
  useGetSaleVehicleAlbumPhotoCheckerReportQuery,
  useGetSaleVehicleAlbumPhotoListQuery,
  useGetSaleVehicleQuery,
  useListSaleVehicleAlbumsByVehicleQuery,
  useUpdateSaleVehiclePhotoAlbumMutation,
} from '@dms/api/vehicle';
import {featureFlags} from '@dms/feature-flags';
import {getSortableContainerId, ID_DELIMITER, parseDndItemId} from '@dms/features/dnd';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  notificationTypes,
  handleApiError,
  useAdPlatformsVehicleUpdate,
  usePermissions,
} from '@dms/shared';

import {parseDate, useRequiredParams} from 'shared/web';

import {BannerBgRemoval} from '../../components/BannerBgRemoval/BannerBgRemoval';
import {IMAGE_SIZE} from '../../constants/imageSize';
import {useVehicleAlbumWatermarkPreview} from '../../hooks/useVehicleAlbumWatermarkPreview';
import {AdUpdateAlert} from './AdUpdateAlert';
import {AdUpdateNavigationBlocker} from './AdUpdateNavigationBlocker';
import {Album} from './Album';
import {AlbumImage} from './AlbumImage';
import {BackgroundRemovalStatus} from './BackgroundRemovalStatus';
import {useFinancingRenderWindow} from './hooks/useFinancingRenderWindow';
import {PhotoCheckerWidget} from './PhotoCheckerWidget';
import {getNotificationText} from './utils/getNotificationText';
import {getPhotoCheckerNotificationAlbumId} from './utils/getPhotoCheckerNotificationAlbumId';
import {buildPhotoEvalMap} from './utils/matchPhotoEval';
import {orderSaleVehicleAlbums} from './utils/orderSaleVehicleAlbums';
import {prepareAlbumImageUrl} from './utils/prepareAlbumImageUrl';

const PHOTO_CHECKER_WIDGET_WIDTH = 84;

// Sortable gallery ids embed the signed image URL (with its token), and dnd-kit's default
// announcements write the raw ids into a live region. Announce only the photo id instead.
const toAnnouncedDndId = (dndId: UniqueIdentifier) =>
  typeof dndId === 'string' && dndId.startsWith(`sortable${ID_DELIMITER}`)
    ? parseDndItemId(dndId).id
    : dndId;

const toAnnouncedDndItem = <T extends {id: UniqueIdentifier}>(item: T): T => ({
  ...item,
  id: toAnnouncedDndId(item.id),
});

const GALLERY_DRAG_ANNOUNCEMENTS: Announcements = {
  onDragStart: ({active}) => defaultAnnouncements.onDragStart({active: toAnnouncedDndItem(active)}),
  onDragOver: ({active, over}) =>
    defaultAnnouncements.onDragOver({
      active: toAnnouncedDndItem(active),
      over: isNil(over) ? null : toAnnouncedDndItem(over),
    }),
  onDragEnd: ({active, over}) =>
    defaultAnnouncements.onDragEnd({
      active: toAnnouncedDndItem(active),
      over: isNil(over) ? null : toAnnouncedDndItem(over),
    }),
  onDragCancel: ({active, over}) =>
    defaultAnnouncements.onDragCancel({
      active: toAnnouncedDndItem(active),
      over: isNil(over) ? null : toAnnouncedDndItem(over),
    }),
};

// eslint-disable-next-line max-lines-per-function, max-statements -- Pre-existing violation
export const VehiclePhotoGallery = () => {
  const {id: vehicleId} = useRequiredParams();
  const formatDate = useDateTimeFormatter();

  // useDispatch is not correctly typed, so any is used to override errors thrown from the updateQueryData function
  // eslint-disable-next-line -eslint/no-explicit-any
  const dispatch = useDispatch<any>();

  const [activeDnDUrl, setActiveDnDUrl] = useState<string | null>(null);
  const [isDropAnimationDisabled, setIsDropAnimationDisabled] = useState(false);
  const dragSourceContainerRef = useRef<string | null>(null);
  const isReusingPreviousSalePhotosRef = useRef(false);

  const {data: vehicleParticipation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.vehicle,
    recordId: vehicleId,
  });

  const {data: vehicle} = useGetVehicleQuery({vehicleId});
  const {data: saleVehicle} = useGetSaleVehicleQuery({vehicleId});
  const {data: photoCheckerConfig} = useGetPhotoCheckerConfigQuery();
  const isPhotoCheckerIntegrationEnabled = isTrue(photoCheckerConfig?.enabled);

  const [
    hasRemoveVehiclePhotoBackgroundPermission,
    hasSaleVehicleBackgroundRemovalPermission,
    canReadPhotoCheckerReport,
  ] = usePermissions({
    permissionKeys: [
      'removeVehiclePhotoBackground',
      'saleVehicleBackgroundRemoval',
      'readPhotoCheckerReport',
    ],
    scopes: {
      removeVehiclePhotoBackground: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
    },
  });

  const {data: watermarkList} = useGetWatermarkListQuery();

  const {
    getLightboxProps,
    openWatermarkPreview,
    isLoading: isWatermarkPreviewLoading,
  } = useVehicleAlbumWatermarkPreview();

  const [hasBackgroundRemovalErrorFileOperationIds, setHasBackgroundRemovalErrorFileOperationIds] =
    useState<Set<string>>(new Set());
  const [hasBackgroundRemovalInProgressAlbumsIds, setHasBackgroundRemovalInProgressAlbumsIds] =
    useState<Set<string>>(new Set());

  const [globallySelectedImagesIds, setGloballySelectedImagesIds] = useState<
    Record<string, string[]>
  >({});

  const {hasPublishedAdvertisements, updateAds} = useAdPlatformsVehicleUpdate();
  const [isAdUpdateAlertVisible, setIsAdUpdateAlertVisible] = useState(false);
  const [isAdUpdateInProgress, setIsAdUpdateInProgress] = useState(false);

  const showAdUpdateAlertIfNeeded = async () => {
    const hasAds = await hasPublishedAdvertisements(vehicleId);
    if (hasAds) {
      setIsAdUpdateAlertVisible(true);
    }
  };

  const handleAdUpdateConfirm = () => {
    setIsAdUpdateInProgress(true);
    return updateAds(vehicleId)
      .then((isSuccess) => {
        if (isSuccess) {
          setIsAdUpdateAlertVisible(false);
        }
        return isSuccess;
      })
      .finally(() => setIsAdUpdateInProgress(false));
  };

  const handleRefetchSaleVehiclePhotos = () => {
    dispatch(
      saleVehiclePhotoApi.util.invalidateTags([
        'SaleVehicleAlbumPhotos',
        'SaleVehicleAlbums',
        'Vehicle',
        'SaleVehicleFlags',
      ])
    );
  };

  const invalidatePhotoCheckerReport = (saleVehicleAlbumId: string) => {
    dispatch(
      saleVehiclePhotoApi.util.invalidateTags([
        {type: 'PhotoCheckerReport', id: saleVehicleAlbumId},
      ])
    );
  };

  useSubscribeToNotificationsQuery((notification) => {
    if (notification.type === 'file_storage-file_uploaded') {
      handleRefetchSaleVehiclePhotos();
    }

    if (notification.type === notificationTypes.SALE_VEHICLE_BACKGROUND_REMOVAL_FINISHED) {
      handleRefetchSaleVehiclePhotos();
    }

    const photoCheckerAlbumId = getPhotoCheckerNotificationAlbumId(notification);

    if (isNotNil(photoCheckerAlbumId)) {
      invalidatePhotoCheckerReport(photoCheckerAlbumId);
      return;
    }

    if (notification.variant === 'background') {
      const notificationText = getNotificationText(notification);
      if (isNotNil(notificationText)) {
        showNotification.success(notificationText);
      }

      dispatch(
        saleVehiclePhotoApi.util.invalidateTags([
          'SaleVehicleAlbumPhotos',
          'SaleVehicleAlbums',
          'Vehicle',
          'SaleVehicleFlags',
        ])
      );
    }
  });

  const {
    data: albums,
    isLoading: areAlbumsLoading,
    isError: hasAlbumsError,
  } = useListSaleVehicleAlbumsByVehicleQuery({
    vehicleId,
  });

  const [currentAlbums, previousAlbums] = partition(
    (album) => isTrue(album.active),
    (albums ?? []).filter((album) => album.name !== VehicleAlbums.PHOTOS_SERVICE_CASE)
  );

  const activeAlbums = orderSaleVehicleAlbums(currentAlbums);

  const previousAlbumIds = previousAlbums.map((album) => album.id);

  const previousAlbumsGroupedByDate = groupBy(
    (album) => album.saleVehicleCreatedAt,
    previousAlbums
  );

  const salesAlbum = activeAlbums.find((album) => album.name === VehicleAlbums.PHOTOS_CAR_SALES);

  const financingRenderWindow = useFinancingRenderWindow(salesAlbum?.saleVehicleId);

  const {data: salesAlbumPhotos, isLoading: isLoadingSalesAlbumPhotos} =
    useGetSaleVehicleAlbumPhotoListQuery(
      isNotNil(salesAlbum)
        ? {saleVehicleId: salesAlbum.saleVehicleId, saleVehicleAlbumId: salesAlbum.id}
        : skipToken
    );

  const hasPhotoCheckerPhotos = !isLoadingSalesAlbumPhotos && (salesAlbumPhotos?.length ?? 0) > 0;

  const latestPreviousSalesAlbum = head(
    sortWith(
      [descend((album) => parseDate(album.saleVehicleCreatedAt).getTime())],
      previousAlbums.filter((album) => album.name === VehicleAlbums.PHOTOS_CAR_SALES)
    )
  );

  const isSalesAlbumEmpty =
    isNotNil(salesAlbum) && !isLoadingSalesAlbumPhotos && isNilOrEmpty(salesAlbumPhotos);

  const {data: previousSalesAlbumPhotos} = useGetSaleVehicleAlbumPhotoListQuery(
    isSalesAlbumEmpty && isNotNil(latestPreviousSalesAlbum)
      ? {
          saleVehicleId: latestPreviousSalesAlbum.saleVehicleId,
          saleVehicleAlbumId: latestPreviousSalesAlbum.id,
        }
      : skipToken
  );

  const canReusePreviousSalePhotos = isSalesAlbumEmpty && isNonEmptyArray(previousSalesAlbumPhotos);

  const {data: photoCheckerReport} = useGetSaleVehicleAlbumPhotoCheckerReportQuery(
    isNotNil(salesAlbum) && canReadPhotoCheckerReport && isPhotoCheckerIntegrationEnabled
      ? {saleVehicleAlbumId: salesAlbum.id}
      : skipToken
  );

  const photoEvalMap = photoCheckerReport?.result?.evals
    ? buildPhotoEvalMap(photoCheckerReport.result.evals)
    : {};

  const [copySaleVehiclePhoto, {isLoading: isCopyingSaleVehiclePhoto}] =
    useCopySaleVehiclePhotoMutation();
  const [updateSaleVehicleAlbum] = useUpdateSaleVehiclePhotoAlbumMutation();

  const copyImage = ({
    fileIds,
    sourceAlbumId,
    destinationAlbum,
    saleVehicleId,
  }: {
    fileIds: string[];
    saleVehicleId: string;
    sourceAlbumId: string;
    destinationAlbum: {
      id: string;
      name: string;
    };
    position?: number;
  }) => {
    if (isNonEmptyArray(fileIds)) {
      return copySaleVehiclePhoto({
        saleVehicleId,
        saleVehiclePhotoCopyRequestBody: {
          photos: fileIds.map((fileId, index) => ({
            fileId,
            position: index,
          })),
          sourceAlbum: sourceAlbumId,
          destinationAlbum: destinationAlbum.id,
        },
      })
        .unwrap()
        .then(() => {
          financingRenderWindow.startRenderWindow();
          if (destinationAlbum.name === VehicleAlbums.PHOTOS_CAR_SALES) {
            void showAdUpdateAlertIfNeeded();
          }
        })
        .catch(handleApiError);
    }
  };

  const handleReusePreviousSalePhotos = () => {
    if (
      isReusingPreviousSalePhotosRef.current ||
      isNil(salesAlbum) ||
      isNil(latestPreviousSalesAlbum) ||
      !isNonEmptyArray(previousSalesAlbumPhotos)
    ) {
      return;
    }

    isReusingPreviousSalePhotosRef.current = true;

    const copyResult = copyImage({
      fileIds: sortWith([ascend((photo) => photo.weight)], previousSalesAlbumPhotos).map(
        (photo) => photo.id
      ),
      sourceAlbumId: latestPreviousSalesAlbum.id,
      destinationAlbum: {id: salesAlbum.id, name: VehicleAlbums.PHOTOS_CAR_SALES},
      saleVehicleId: salesAlbum.saleVehicleId,
    });

    if (isNil(copyResult)) {
      isReusingPreviousSalePhotosRef.current = false;
      return;
    }

    void copyResult.finally(() => {
      isReusingPreviousSalePhotosRef.current = false;
    });
  };

  const moveImage = ({
    fileIds,
    sourceAlbumId,
    destinationAlbum,
  }: {
    fileIds: string[];
    sourceAlbumId: string;
    destinationAlbum: {
      id: string;
      name: string;
    };
  }) => {
    if (isNonEmptyArray(fileIds)) {
      return updateSaleVehicleAlbum({
        saleVehiclePhotoUpdateAlbumRequestBody: {
          photos: fileIds.map((fileId, index) => ({
            fileId,
            position: index,
          })),
          sourceAlbum: sourceAlbumId,
          destinationAlbum: destinationAlbum.id,
        },
      })
        .unwrap()
        .then(() => {
          financingRenderWindow.startRenderWindow();
          if (destinationAlbum.name === VehicleAlbums.PHOTOS_CAR_SALES) {
            void showAdUpdateAlertIfNeeded();
          }
        })
        .catch(handleApiError);
    }
  };

  const buildContextMenu = (payload: {
    images: string[];
    sourceAlbumId: string;
    onSuccess?: () => void;
    onEventFired?: () => void;
    isSalesAlbum?: boolean;
  }) => (
    <>
      <DropdownSubmenu
        label={i18n.t('general.actions.copyTo')}
        prefix={<Icon value="file/folder" />}
        suffix={<Icon value="navigation/arrow_right" />}
      >
        {activeAlbums?.map((album) => (
          <Show key={`copy_${album.id}`} when={!(album.id === payload.sourceAlbumId)}>
            <DropdownItem
              label={album.translation}
              onClick={() => {
                payload.onEventFired?.();
                copyImage({
                  fileIds: payload.images || [],
                  sourceAlbumId: payload.sourceAlbumId,
                  destinationAlbum: {
                    id: album.id,
                    name: album.name,
                  },
                  saleVehicleId: album.saleVehicleId,
                  position: 0,
                })?.then(() => payload.onSuccess?.());
              }}
            />
          </Show>
        ))}
      </DropdownSubmenu>
      <DropdownSubmenu
        label={i18n.t('general.actions.moveTo')}
        prefix={<Icon value="file/folder" />}
        suffix={<Icon value="navigation/arrow_right" />}
      >
        {activeAlbums?.map((album) => (
          <Show key={`copy_${album.id}`} when={!(album.id === payload.sourceAlbumId)}>
            <DropdownItem
              label={album.translation}
              onClick={() => {
                payload.onEventFired?.();
                if (previousAlbumIds.includes(payload.sourceAlbumId)) {
                  return copyImage({
                    fileIds: payload.images || [],
                    sourceAlbumId: payload.sourceAlbumId,
                    destinationAlbum: {
                      id: album.id,
                      name: album.name,
                    },
                    saleVehicleId: album.saleVehicleId,
                    position: 0,
                  })?.then(() => payload.onSuccess?.());
                }
                return moveImage({
                  fileIds: payload.images || [],
                  sourceAlbumId: payload.sourceAlbumId,
                  destinationAlbum: {
                    id: album.id,
                    name: album.name,
                  },
                })?.then(() => payload.onSuccess?.());
              }}
            />
          </Show>
        ))}
      </DropdownSubmenu>
      <Show
        when={
          payload.images.length === 1 &&
          payload.isSalesAlbum &&
          isNotNilOrEmpty(watermarkList) &&
          isNotNilOrEmpty(saleVehicle?.watermarkId)
        }
      >
        <DropdownItem
          label={i18n.t('entity.vehicle.labels.showWithWatermark')}
          prefix={<Icon value="AV/branding_watermark" />}
          onClick={() => openWatermarkPreview(payload.images[0])}
        />
      </Show>
    </>
  );

  // useCallback with [] is required for correctness, not performance: these callbacks are
  // passed as props to Album where they appear in useEffect dependency arrays. Without a
  // stable reference, every parent re-render creates a new function, the child effect fires,
  // calls setState here, triggering another re-render — infinite loop.
  const handleBackgroundRemoval = useCallback(
    (albumId: string, isInProgress: boolean, hasErrorOperationIds?: string[]) => {
      if (hasErrorOperationIds?.length) {
        setHasBackgroundRemovalErrorFileOperationIds(
          (prev) => new Set([...prev, ...hasErrorOperationIds])
        );
      }
      if (isInProgress) {
        setHasBackgroundRemovalInProgressAlbumsIds((prev) => new Set([...prev, albumId]));
      } else {
        setHasBackgroundRemovalInProgressAlbumsIds((prev) => {
          const newInProgressAlbums = new Set(prev);
          newInProgressAlbums.delete(albumId);
          return newInProgressAlbums;
        });
      }
    },
    []
  );

  // useCallback with [] is required for correctness, not performance: passed as a prop to Album
  // where it appears in a useEffect dependency array. Without a stable reference, every parent
  // re-render creates a new function, the child effect fires, calls setState here, triggering
  // another re-render — infinite loop.
  const handleUpdateGlobalImageSelection = useCallback((albumId: string, ids: string[]) => {
    setGloballySelectedImagesIds((prev) => ({
      ...prev,
      [albumId]: ids,
    }));
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    })
  );

  return (
    <>
      <VStack spacing={4}>
        <Show
          when={
            hasRemoveVehiclePhotoBackgroundPermission &&
            isFeatureEnabled(featureFlags.SALES_BGR_BANNER)
          }
        >
          <BannerBgRemoval />
        </Show>

        <Show
          when={
            hasRemoveVehiclePhotoBackgroundPermission && hasSaleVehicleBackgroundRemovalPermission
          }
        >
          <BackgroundRemovalStatus
            isInProgress={hasBackgroundRemovalInProgressAlbumsIds.size > 0}
            failedOperationsIds={Array.from(hasBackgroundRemovalErrorFileOperationIds)}
            onAcknowledgeOperation={() => {
              setHasBackgroundRemovalErrorFileOperationIds(new Set());
            }}
          />
        </Show>
        <Show when={isAdUpdateAlertVisible}>
          <AdUpdateAlert
            isUpdating={isAdUpdateInProgress}
            onUpdate={handleAdUpdateConfirm}
            data-testid={testIds.vehicles.photos('adUpdateAlert')}
          />
        </Show>
        <AdUpdateNavigationBlocker
          shouldBlock={isAdUpdateAlertVisible}
          isUpdateInProgress={isAdUpdateInProgress}
          onUpdate={handleAdUpdateConfirm}
          data-testid={testIds.vehicles.photos('adUpdateNavigationBlocker')}
        />
        <DataStatus isLoading={areAlbumsLoading} isError={hasAlbumsError}>
          <DndContext
            sensors={sensors}
            accessibility={{announcements: GALLERY_DRAG_ANNOUNCEMENTS}}
            onDragStart={(event) => {
              const {url} = parseDndItemId(event.active.id);
              if (url) {
                setActiveDnDUrl(url);
              }
              dragSourceContainerRef.current = getSortableContainerId(event.active);
              setIsDropAnimationDisabled(false);
            }}
            onDragOver={(event) => {
              if (event.over) {
                const overContainerId =
                  getSortableContainerId(event.over) ?? parseDndItemId(event.over.id).id;
                const isDifferentAlbum = overContainerId !== dragSourceContainerRef.current;
                if (isDifferentAlbum !== isDropAnimationDisabled) {
                  setTimeout(() => setIsDropAnimationDisabled(isDifferentAlbum), 0);
                }
              }
            }}
            onDragEnd={() => {
              dragSourceContainerRef.current = null;
            }}
          >
            {activeAlbums?.map((album) => {
              const isSalesAlbum = album.name === VehicleAlbums.PHOTOS_CAR_SALES;
              const showPhotoChecker =
                isSalesAlbum &&
                canReadPhotoCheckerReport &&
                isPhotoCheckerIntegrationEnabled &&
                hasPhotoCheckerPhotos;

              const albumComponent = (
                <Album
                  key={album.id}
                  isActiveSaleVehicle
                  data-testid={testIds.vehicles.photos(
                    `${album.name === VehicleAlbums.PHOTOS_CAR_PURCHASE ? 'purchase' : 'sales'}-album`
                  )}
                  saleVehicleId={album.saleVehicleId}
                  isSalesAlbum={isSalesAlbum}
                  vehicleId={vehicleId}
                  saleVehicleAlbumId={album.id}
                  title={album.translation}
                  createdAt={
                    album.name === VehicleAlbums.PHOTOS_CUSTOM ? album.createdAt : undefined
                  }
                  canUpload
                  canDelete={album.name === VehicleAlbums.PHOTOS_CUSTOM}
                  showPromotionalPhotos={isSalesAlbum}
                  buildAlbumActionsContextMenu={buildContextMenu}
                  globallySelectedImagesIds={globallySelectedImagesIds}
                  inactiveAlbumIds={previousAlbumIds}
                  onBackgroundRemoval={handleBackgroundRemoval}
                  onUpdateGlobalImageSelection={handleUpdateGlobalImageSelection}
                  canApplyWatermark={isSalesAlbum}
                  onAlbumChange={isSalesAlbum ? showAdUpdateAlertIfNeeded : undefined}
                  isReusingPreviousSalePhotos={isCopyingSaleVehiclePhoto}
                  onReusePreviousSalePhotos={
                    isSalesAlbum && canReusePreviousSalePhotos
                      ? handleReusePreviousSalePhotos
                      : undefined
                  }
                  photoEvalMap={showPhotoChecker ? photoEvalMap : undefined}
                  isFinancingRenderWindowOpen={financingRenderWindow.isRenderWindowOpen}
                  startFinancingRenderWindow={financingRenderWindow.startRenderWindow}
                />
              );

              if (showPhotoChecker) {
                return (
                  <Stack
                    key={album.id}
                    direction={['column', 'column', 'row']}
                    spacing={4}
                    align="flex-start"
                  >
                    <Box flex={1}>{albumComponent}</Box>
                    <Box width={['100%', '100%', PHOTO_CHECKER_WIDGET_WIDTH]}>
                      <PhotoCheckerWidget
                        saleVehicleAlbumId={album.id}
                        photosUpdatedAt={album.photosUpdatedAt}
                        albumCreatedAt={album.createdAt}
                        automaticModeEnabledAt={photoCheckerConfig?.automaticModeEnabledAt}
                        mode={photoCheckerConfig?.mode ?? 'manual'}
                        data-testid={testIds.vehicles.photos('photo-checker-widget')}
                      />
                    </Box>
                  </Stack>
                );
              }

              return albumComponent;
            })}
            <Show when={previousAlbums?.length > 0}>
              <Heading size={3}>{i18n.t('entity.vehicle.labels.previousPhotos')}</Heading>
              <Text size="xSmall">{i18n.t('entity.vehicle.labels.previousPhotosExplainer')}</Text>
              {Object.entries(previousAlbumsGroupedByDate).map(([date, albums]) => (
                <div key={date}>
                  <Heading size={4}>
                    {i18n.t(`entity.vehicle.albums.createdAt`, {
                      date: formatDate('dateShort', parseDate(date)),
                    })}
                  </Heading>
                  {orderSaleVehicleAlbums(albums ?? []).map((album) => (
                    <Box key={album.id} paddingTop={4}>
                      <Album
                        data-testid={testIds.vehicles.photos(
                          `${album.name === 'PHOTOS_CAR_PURCHASE' ? 'purchase' : 'sales'}-previous-album`
                        )}
                        vehicleId={vehicleId}
                        isActiveSaleVehicle={false}
                        saleVehicleId={album.saleVehicleId}
                        saleVehicleAlbumId={album.id}
                        title={album.translation}
                        createdAt={
                          album.name === VehicleAlbums.PHOTOS_CUSTOM ? album.createdAt : undefined
                        }
                        canUpload={false}
                        inactiveAlbumIds={previousAlbumIds}
                        isSalesAlbum={album.name === VehicleAlbums.PHOTOS_CAR_SALES}
                        buildAlbumActionsContextMenu={buildContextMenu}
                        onBackgroundRemoval={handleBackgroundRemoval}
                        onUpdateGlobalImageSelection={handleUpdateGlobalImageSelection}
                      />
                    </Box>
                  ))}
                </div>
              ))}
            </Show>
            <DragOverlay dropAnimation={isDropAnimationDisabled ? null : undefined}>
              <Show when={isNotNil(activeDnDUrl)}>
                <Box width={IMAGE_SIZE.width} height={IMAGE_SIZE.height}>
                  <AlbumImage id="sortable:dragOverlay" src={prepareAlbumImageUrl(activeDnDUrl!)} />
                </Box>
              </Show>
            </DragOverlay>
          </DndContext>
          <Lightbox
            data-testid={testIds.vehicles.photos('watermarkPreview')}
            {...getLightboxProps()}
          />
        </DataStatus>
      </VStack>
      <Show when={isWatermarkPreviewLoading}>
        <Spinner variant="overlay" />
      </Show>
    </>
  );
};
